---
title: Konfiguracija obsega
sidebar_position: 5
description: Konfiguracija obsega
---

# Konfiguracija in prilagajanje obsega

## Pregled

Vsak obseg (Scope) v aplikaciji AM App ima prilagodljive nastavitve, ki določajo, katere funkcije, sloji in privzete vrednosti so na voljo. Konfiguracija je shranjena za vsak obseg posebej v skrbniški storitvi. Vgrajeni **Urejevalnik konfiguracije** (Config Editor) omogoča skrbnikom z vlogo **SUPER-SYSADMIN**, da konfiguracijo urejajo neposredno v aplikaciji. Končni uporabniki brez te vloge Urejevalnika konfiguracije ne vidijo.

#### Zakaj je konfiguracija obsega pomembna

- Različne kmetijske regije imajo različne potrebe po spremljanju
- Obsege je mogoče prilagoditi določenim primerom uporabe, posevkom, sezonam ali pravilom skladnosti
- Konfiguracija določa, kaj vidite in kako delate s podatki

**Pomembno**: Kot končni uporabnik konfiguracije obsega ne morete neposredno spreminjati. Konfiguracijo upravljajo skrbniki. Razumevanje tega, kaj je mogoče nastaviti, pa vam pomaga razumeti, zakaj se obsegi obnašajo različno.

## Urejanje konfiguracije obsega

**Urejevalnik konfiguracije** prikazuje surovi shranjeni JSON obsega in ponuja:

- Urejevalnik kode JSON s poudarjanjem skladnje
- Sprotno validacijo glede na shemo konfiguracije — neveljavni popravki preprečijo shranjevanje, tako da so napake sheme zaznane, preden dosežejo uporabnike
- Opozorilo ob neshranjenih spremembah
- Gumb **Shrani**, ki posodobljeno konfiguracijo takoj zapiše nazaj v obseg

Za dostop: odprite stranski meni in izberite **Urejevalnik konfiguracije**.

## Nastavljivi elementi za posamezen obseg

Za vsak obseg je mogoče prilagoditi naslednje elemente:

---

### Privzete vrednosti časovnega obdobja

**Kaj nadzoruje**:

- Privzeti datum začetka in konca ob odprtju obsega
- Običajno nastavljeno na trenutno rastno sezono ali obdobje spremljanja

**Vpliv na uporabnika**:

- Ob prvi izbiri obsega je časovno obdobje že prednastavljeno
- Te privzete vrednosti lahko prepišete prek panela **Možnosti**
- Privzete vrednosti zagotavljajo, da gledate ustrezne podatke

**Primer**:

```json
{
  "options": {
    "timerange": {
      "start": "2023-01-01",
      "end": "2023-12-31"
    }
  }
}
```

---

### Nastavitve zemljevida

**Kaj nadzoruje**:

1. **Razpoložljivi osnovni sloji** (`map.baseLayers`):
   - Kateri ploščični sloji so na voljo v meniju **Sloji**
   - Podprti so trije tipi virov: `WMS`, `WMTS` in `XYZ` — nastavijo se prek polja `type` pri vsakem vnosu
   - Vsi trije tipi imajo skupni polji `id` (enolični identifikator) in `label` (prikazno ime v meniju **Sloji**)
   - Polja, specifična za posamezen tip:
     - **WMS**: `params.LAYERS` (obvezno — ime sloja kot niz ali polje); izbirno `url` (ena končna točka) ali `urls` (polje končnih točk), `serverType`, `projection`, `tileGrid`
     - **WMTS**: `layer`, `style`, `matrixSet`, `tileGrid` (z `origin`, `tileSize`, `resolutions`, `matrixIds`); izbirno `url`, `format`, `projection`
     - **XYZ**: izbirno `url` (predloga URL-ja ploščice z ograditelji `{z}`, `{x}`, `{y}`) ali `urls` (polje predlog), `maxZoom`, `minZoom`, `projection`
   - Vsi tipi sprejemajo izbirno polje `attributions` (niz, prikazan v kontrolniku navedbe virov na zemljevidu)

2. **Prag približanja za FOI** (`map.foiZoomThreshold`):
   - Stopnja približanja, pri kateri FOI postanejo vidni na zemljevidu
   - Preprečuje natrpanost ob oddaljitvi
   - Tipična vrednost: stopnja približanja 12–14

3. **Prag približanja za oznake po tipu FOI** (`map.labelZoomThresholdPerFoiType`):
   - Stopnja približanja za vsak tip FOI, pri kateri se na zemljevidu prikažejo oznake FOI
   - Omogoča natančno upravljanje oznak, kadar ima obseg več tipov FOI z različno gostoto

4. **Sistem merskih enot** (`map.measurement`):
   - Nadzoruje **samo enote za površino**: metrične (m²/km² samodejno prilagojene ali hektarji) ali imperialne (akri)
   - Razdalja je vedno prikazana v metrih ali kilometrih, ne glede na to nastavitev

**Vpliv na uporabnika**:

- Različni obsegi ponujajo različne osnovne sloje
- Nekateri obsegi lahko uporabljajo imperialne enote za površino (npr. projekti v ZDA)
- Vidnost FOI in prikaz oznak se spreminjata glede na stopnjo približanja

**Primer**:

```json
{
  "map": {
    "baseLayers": [
      {
        "id": "world_imagery",
        "label": "World Imagery",
        "type": "XYZ",
        "url": "https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        "attributions": "Tiles © ArcGIS",
        "maxZoom": 19
      },
      {
        "id": "my_wms_layer",
        "label": "National Orthophoto (WMS)",
        "type": "WMS",
        "url": "https://example.com/ows",
        "params": {
          "LAYERS": "orthophoto_2023",
          "VERSION": "1.3.0"
        },
        "attributions": "© National Mapping Agency"
      },
      {
        "id": "ORTHOIMAGERY.ORTHOPHOTOS.BDORTHO",
        "url": "https://data.geopf.fr/wmts",
        "type": "WMTS",
        "label": "Aerial imagery 50 cm",
        "layer": "ORTHOIMAGERY.ORTHOPHOTOS.BDORTHO",
        "style": "normal",
        "format": "image/jpeg",
        "tileGrid": {
          "origin": [-20037508.342789244, 20037508.342789244],
          "tileSize": 256,
          "matrixIds": [
            "0",
            "1",
            "2",
            "3",
            "4",
            "5",
            "6",
            "7",
            "8",
            "9",
            "10",
            "11",
            "12",
            "13",
            "14",
            "15",
            "16",
            "17",
            "18"
          ],
          "resolutions": [
            156543.03392804097, 78271.51696402048, 39135.75848201024, 19567.87924100512, 9783.93962050256,
            4891.96981025128, 2445.98490512564, 1222.99245256282, 611.49622628141, 305.748113140705,
            152.8740565703525, 76.43702828517625, 38.21851414258813, 19.109257071294063, 9.554628535647032,
            4.777314267823516, 2.388657133911758, 1.194328566955879, 0.5971642834779395
          ]
        },
        "matrixSet": "PM_6_18",
        "projection": "EPSG:3857",
        "attributions": "© IGN"
      }
    ],
    "foiZoomThreshold": 14,
    "labelZoomThresholdPerFoiType": {
      "PARCEL": 13,
      "HOLDING": 10
    },
    "measurement": {
      "system": "metric",
      "areaUnit": "sqm-km2-auto"
    }
  }
}
```

---

### Projekcije zemljevida

**Kaj nadzoruje**:

Koordinatne referenčne sisteme (CRS) po meri, ki jih zemljevid podpira za ta obseg. Aplikacija privzeto uporablja Web Mercator (EPSG:3857). Kadar so podatki obsega shranjeni v nacionalni projekciji — kot je češki S-JTSK (EPSG:5514) ali francoski Lambert-93 (EPSG:2154) — mora biti ustrezen CRS registriran tukaj, da se geometrije pravilno prikažejo in izmerijo.

Vsak vnos v polju `projections` ima:

| Polje        | Obvezno | Opis                                                              |
| ------------ | ------- | ----------------------------------------------------------------- |
| `crs`        | Da      | Identifikator CRS, npr. `"EPSG:5514"`                             |
| `definition` | Da      | Definicijski niz Proj4 za ta CRS                                  |
| `extent`     | Ne      | Mejni okvir v izvornih enotah CRS: `minX`, `minY`, `maxX`, `maxY` |

**Vpliv na uporabnika**:

- Omogoča pravilen izris geometrij FOI, shranjenih v nacionalni projekciji
- Omogoča natančno merjenje površin in razdalj v izvornih enotah obsega
- Brez registrirane ustrezne projekcije se lahko geometrije na zemljevidu prikažejo popačeno ali zamaknjeno

**Primer**:

```json
{
  "projections": [
    {
      "crs": "EPSG:5514",
      "definition": "+proj=krovak +lat_0=49.5 +lon_0=24.8333333333333 +alpha=30.2881397527778 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=589,76,480,0,0,0,0 +units=m +no_defs +type=crs",
      "extent": {
        "minX": -951499.37,
        "minY": -1250000.0,
        "maxX": -159365.31,
        "maxY": -915744.49
      }
    }
  ]
}
```

---

### Privzete nastavitve sloja zemljevida po tipu FOI

**Kaj nadzoruje**:

Vsak tip FOI ima lahko svoje privzete nastavitve prikaza na zemljevidu prek `map.layerOptions`. Ključ je koda tipa FOI; vsak vnos nadzoruje:

| Lastnost           | Tip                                   | Opis                                                               |
| ------------------ | ------------------------------------- | ------------------------------------------------------------------ |
| `visible`          | boolean                               | Ali je ta tip FOI privzeto viden na zemljevidu                     |
| `border`           | boolean                               | Ali se izrišejo robovi FOI                                         |
| `fill`             | boolean                               | Ali so poligoni FOI zapolnjeni                                     |
| `opacity`          | število (0–100)                       | Odstotek prosojnosti polnila                                       |
| `labels`           | polje nizov                           | Ključi atributov, ki se prikažejo kot oznake na zemljevidu         |
| `colorBy`          | `fixedColor` / `attribute` / `marker` | Način obarvanja poligonov FOI                                      |
| `fixedColor`       | niz (nullable)                        | Šestnajstiška barva, uporabljena ko je `colorBy` enak `fixedColor` |
| `colorByAttribute` | niz (nullable)                        | Ključ atributa, uporabljen ko je `colorBy` enak `attribute`        |
| `colorByMarker`    | niz (nullable)                        | Koda tipa Markerja, uporabljena ko je `colorBy` enak `marker`      |

**Vpliv na uporabnika**:

- Ob odprtju obsega se tipi FOI na zemljevidu prikažejo z barvami, polnili in oznakami, ki jih je nastavil skrbnik
- Te privzete nastavitve lahko prepišete v panelu možnosti vizualizacije FOI — vaše osebne nastavitve imajo prednost
- Če ima obseg več tipov FOI, ima lahko vsak privzeto svoj poseben vizualni slog

**Primer**:

```json
{
  "map": {
    "layerOptions": {
      "PARCEL": {
        "visible": true,
        "border": true,
        "fill": true,
        "opacity": 60,
        "labels": ["ref_id"],
        "colorBy": "marker",
        "colorByMarker": "ARPS_HOMOGENEITY_MARKER"
      },
      "HOLDING": {
        "visible": true,
        "border": true,
        "fill": false,
        "colorBy": "fixedColor",
        "fixedColor": "#FFD700"
      }
    }
  }
}
```

---

### Integracija s Sentinel Hub

**Kaj nadzoruje**:

- **ID instance**: Identifikator za dostop do slojev Sentinel Hub
- **Razpoložljivi sloji opazovanj (Observation)**: Katere vizualizacije so na voljo
  - Primeri: NDVI, True Color, False Color, EVI, LAI itd.
  - Vsak obseg ima lahko enkraten nabor slojev, opredeljen v konfiguracijah platforme Planet Insights Platform

**Vpliv na uporabnika**:

- Sloji opazovanj, ki jih vidite v panelu **Možnosti**, se razlikujejo glede na obseg
- Nekateri obsegi imajo veliko slojev, drugi le nekaj
- Razpoložljivost slojev je odvisna od potreb projekta in konfiguracije Sentinel Hub

**Primer**:

```json
{
  "sentinelHub": {
    "shInstanceId": "81b306fe-0435-44bd-9f1f-d516999a8e97"
  }
}
```

---

### Konfiguracija signalov

Za vsak tip signala lahko konfiguracija obsega določa:

**Kaj nadzoruje**:

1. **Kateri tipi signalov so na voljo**: Obseg določa, kateri signali se izračunajo
2. **Privzeta vidnost v vizualizaciji signalov in Markerjev**: Ali je signal privzeto prikazan
3. **Privzeta vidnost v opisih orodij**: Ali se signal privzeto prikaže v opisih orodij
4. **Barvne sheme**: Barve za svetli in temni način za vsak signal (en barvni niz ali polje barv za gradientne signale)
5. **Prepisi veljavnih tipov signalov** (`options.validSignalTypeOverrides`): Preslikajo, kateri tip signala se uporabi za odločitev, ali je opazovanje veljavno. Vsak vnos je ključen s kodo tipa signala; vrednost je nadomestna koda tipa signala, ki se uporabi namesto njega, ali `null` za izklop filtriranja veljavnosti za ta tip.

**Vpliv na uporabnika**:

- Vaš gradnik **Vizualizacija signalov in Markerjev** bo prikazal signale, opredeljene za vaš obseg
- Nekateri signali so lahko privzeto skriti (vklopite jih v legendi)
- Barve signalov so znotraj obsega dosledne

**Primer**:

```json
{
  "signals": {
    "DEMO_FR23_S2_NDVI": {
      "sigMaVisShow": true,
      "sigMaVisTooltip": true,
      "color": {
        "dark": "#000000",
        "light": "#000000"
      }
    }
  }
}
```

Primer prepisa veljavnega signala (uporabi veljavnost `S2_NDVI` za `PF_NDVI` in izklopi filtriranje veljavnosti za `S2_EVI`):

```json
{
  "options": {
    "validSignalTypeOverrides": {
      "PF_NDVI": "S2_NDVI",
      "S2_EVI": null
    }
  }
}
```

---

### Konfiguracija Markerjev

Za vsak tip Markerja lahko konfiguracija obsega določa:

**Kaj nadzoruje**:

1. **Kateri tipi Markerjev so na voljo**: Obseg določa, kateri Markerji se izračunajo
2. **Privzeta vidnost v vizualizaciji signalov in Markerjev**: Privzeto prikazano ali skrito
3. **Privzeta vidnost v opisih orodij**: Privzeto v opisih orodij ali ne
4. **Barvne sheme**: Barve za svetli in temni način (en barvni niz ali polje za gradiente)
5. **Sheme obarvanja na podlagi atributov za vizualizacijo na zemljevidu** (`layerColourScheme`):
   - Določa preslikave vrednosti v barve
   - Primer: Barve semaforja (zelena, rumena, rdeča)
   - Uporablja se pri obarvanju FOI glede na rezultate Markerjev

**Vpliv na uporabnika**:

- Razpoložljivi Markerji se razlikujejo glede na obseg
- Možnosti obarvanja FOI na zemljevidu so odvisne od konfiguracije Markerjev
- Barve Markerjev v vizualizacijah ustrezajo nastavitvam obsega

**Primer**:

```json
{
  "markers": {
    "ARPS_HOMOGENEITY_MARKER": {
      "sigMaVisShow": false,
      "sigMaVisTooltip": false,
      "color": {
        "dark": "#000000",
        "light": "#000000"
      },
      "layerColourScheme": [
        {
          "value": "HOMOGENEOUS",
          "colour": "#009688"
        },
        {
          "value": "HETEROGENEOUS",
          "colour": "#FFC107"
        }
      ]
    }
  }
}
```

---

### Prikaz atributov FOI

**Kaj nadzoruje**:

Ključ `foiAttributes` nadzoruje, kateri dodatni atributi FOI so v aplikaciji izpostavljeni in kako so prikazani. Gre za zapis, ključen s kodo tipa FOI (npr. `"PARCEL"`), kjer je vsaka vrednost polje vnosov za prikaz atributov.

Vsak vnos v polju ima:

| Polje                  | Obvezno                        | Opis                                                                                  |
| ---------------------- | ------------------------------ | ------------------------------------------------------------------------------------- |
| `attributeKey`         | Da                             | Identifikator atributa za konfiguracijo                                               |
| `preview`              | Ne                             | Nadzoruje vidnost v panelu **Predogled podrobnosti** (Predogled izbire) na zemljevidu |
| `preview.collapsed`    | Ne                             | `true` za prikaz tega atributa v strnjenem (povzetkovnem) stanju panela               |
| `preview.expanded`     | Ne                             | `true` za prikaz tega atributa ob razširitvi panela                                   |
| `sigMaVis`             | Ne                             | Izriše atribut kot statični referenčni Marker v grafu signalov in Markerjev           |
| `sigMaVis.label`       | Da (če je sigMaVis nastavljen) | Prikazna oznaka za referenčno črto                                                    |
| `sigMaVis.show`        | Da (če je sigMaVis nastavljen) | Ali je referenčna črta privzeto prikazana                                             |
| `sigMaVis.tooltip`     | Da (če je sigMaVis nastavljen) | Ali se referenčna črta prikaže v opisih orodij grafa                                  |
| `sigMaVis.color.light` | Da (če je sigMaVis nastavljen) | Šestnajstiška barva za svetli način                                                   |
| `sigMaVis.color.dark`  | Da (če je sigMaVis nastavljen) | Šestnajstiška barva za temni način                                                    |

**Vpliv na uporabnika**:

- Atributi, nastavljeni s `preview`, se prikažejo v plavajočem panelu **Predogled podrobnosti**, ki se odpre, ko na zemljevidu kliknete na FOI (glejte [Panel Predogled podrobnosti](./02-getting-started)). Zastavici `collapsed` / `expanded` nadzorujeta gostoto informacij: ključni identifikatorji vidni na prvi pogled v primerjavi s celotnim naborom ob razširitvi panela.
- Atributi, nastavljeni s `sigMaVis`, se prikažejo kot vodoravne referenčne črte v grafu signalov in Markerjev skupaj s časovnimi vrstami signalov in Markerjev (glejte [Modul pogleda FOI](./03-foi-view-module)) — uporabno za prikaz statične vrednosti (npr. prijavljena površina, referenčni NDVI) neposredno v grafu.

**Primer**:

```json
{
  "foiAttributes": {
    "PARCEL": [
      {
        "attributeKey": "declared_area_ha",
        "preview": {
          "collapsed": true,
          "expanded": true
        },
        "sigMaVis": {
          "label": "Declared area (ha)",
          "show": true,
          "tooltip": true,
          "color": {
            "light": "#007C91",
            "dark": "#00CBE5"
          }
        }
      },
      {
        "attributeKey": "farmer_name",
        "preview": {
          "collapsed": false,
          "expanded": true
        }
      }
    ]
  }
}
```

---

### Nastavitve večosne vizualizacije

**Kaj nadzoruje**:

1. **Vklop/izklop večosnih grafov**: Vklopite ali izklopite funkcijo več osi
2. **Konfiguracija do 3 osi**: Določite do 3 osi Y
3. **Lastnosti osi**:
   - **Položaj**: Leva ali desna stran grafa
   - **Domena**: Najmanjša in največja vrednost osi
   - **Oznake**: Izbirne oznake osi
   - **Dodelitve tipov signalov**: Kateri signali se izrišejo na kateri osi

**Vpliv na uporabnika**:

- Če je funkcija vklopljena, lahko vaša **Vizualizacija signalov in Markerjev** prikaže več osi
- Signali z različnimi razponi vrednosti (npr. 0–1 za NDVI, 0–900 za CCC) se izrišejo na ločenih oseh
- Olajša primerjavo različnih signalov

**Omejitve**:

- Skupaj največ 3 osi
- Največ 2 osi na vsaki strani (leva ali desna)
- ID-ji osi morajo biti enolični

**Primer**:

```json
{
  "sigMaVisAxes": {
    "enabled": true,
    "axes": [
      {
        "id": "land surface temperature",
        "position": "right",
        "domain": [0, 45],
        "tickCount": 10,
        "signalTypeCodes": ["LST_10M_AVG", "LST_10M_CLIM", "LST_10M_V2_LST_C"]
      }
    ]
  }
}
```

---

### Integracija fotografij

**Kaj nadzoruje**:

1. **Vklop/izklop geooznačenih fotografij**: Vklopite ali izklopite prikaz fotografij
2. **ID stranke za dostop do storitve fotografij**: Identifikator za dostop do podatkov o fotografijah

**Vpliv na uporabnika**:

- Če je funkcija vklopljena, se geooznačene fotografije prikažejo na zemljevidu
- Fotografije nudijo vizualni kontekst terenskih podatkov
- Vsi obsegi nimajo dostopa do fotografij

**Primer**:

```json
{
  "photos": {
    "show": true,
    "customerId": "YOUR_CUSTOMER_ID"
  }
}
```

---

### Generiranje poročil PDF

**Kaj nadzoruje**:

- Ali se za ta obseg v Validacijski konzoli prikaže zavihek **Poročila**
- Omogoči se z nastavitvijo `reporting.pdfGeneration.show` na `true`

**Vpliv na uporabnika**:

- Ko je funkcija omogočena, se v levem stolpcu Validacijske konzole prikaže zavihek **Poročila**
- Uporabniki lahko izberejo opazovanja in neposredno iz Validacijske konzole generirajo poročilo PDF
- Ko je funkcija onemogočena (ali ni nastavljena), se zavihek Poročila ne prikaže

Glejte [Validacijska konzola](./04-validation-console) za postopek generiranja poročila PDF, ko je ta funkcija omogočena za vaš obseg.

**Primer**:

```json
{
  "reporting": {
    "pdfGeneration": {
      "show": true
    }
  }
}
```

## Povzetek vpliva na uporabnika

Različni obsegi imajo lahko popolnoma drugačne:

| Vidik                          | Primer vpliva                                                                                                                      |
| ------------------------------ | ---------------------------------------------------------------------------------------------------------------------------------- |
| **Razpoložljivi sloji**        | Obseg A ima 15 slojev; Obseg B ima 5 slojev                                                                                        |
| **Tipi signalov in Markerjev** | Obseg A spremlja košnjo; Obseg B ne                                                                                                |
| **Obnašanje zemljevida**       | Obseg A uporablja metrične enote; Obseg B imperialne                                                                               |
| **Privzeta časovna obdobja**   | Obseg A: januar–december; Obseg B: maj–september                                                                                   |
| **Dostop do fotografij**       | Obseg A ima fotografije; Obseg B ne                                                                                                |
| **Videz FOI na zemljevidu**    | Obseg A barva parcele po Markerju homogenosti; Obseg B uporablja fiksno barvo                                                      |
| **Poročila PDF**               | Obseg A prikazuje zavihek Poročila; Obseg B ne                                                                                     |
| **Projekcija zemljevida**      | Obseg A uporablja Web Mercator; Obseg B registrira češki S-JTSK za nacionalne podatke                                              |
| **Prikaz atributov FOI**       | Obseg A prikazuje prijavljeno površino v panelu Predogled podrobnosti in kot referenčno črto v grafu; Obseg B ne prikazuje ničesar |

**Ključno spoznanje**: Ko preklopite obseg, se razpoložljive funkcije in podatki aplikacije spremenijo. Vedno preverite konfiguracijo obsega, da razumete, kaj je na voljo.
